import { TypedDocumentNode, gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { MonetaryAmountCurrency } from '@sorare/core/src/lib/monetaryAmount';

import { TransactionsBid } from '../../common/TransactionsBid';
import { AccountEntry } from '../AccountEntry';
import { Auction_authorizationRequest } from './__generated__/index.graphql';

type Props = {
  payment: Auction_authorizationRequest;
};

export const Auction = ({ payment }: Props) => {
  const { operation } = payment;
  const { formatMessage } = useIntl();
  const { fiatCurrency } = useCurrentUserContext();

  if (!isType(operation, 'TokenAuction')) {
    return null;
  }
  const currency = fiatCurrency.code.toLowerCase() as Exclude<
    MonetaryAmountCurrency,
    'wei'
  >;

  return (
    <AccountEntry
      payment={{
        ...payment,
        ...{
          request: {
            ...payment.request,
            ...{
              amount: operation.bestBid?.amounts[currency] || 0,
            },
          },
        },
      }}
      title={formatMessage(glossary.auction)}
    >
      <TransactionsBid tokenBid={operation.bestBid!} />
    </AccountEntry>
  );
};

Auction.fragments = {
  payment: gql`
    fragment Auction_authorizationRequest on AuthorizationRequest {
      id
      ...AccountEntry_authorizationRequest
      operation {
        ... on TokenAuction {
          id
          bestBid {
            id
            amounts {
              eur
              gbp
              usd
            }
            ...TransactionsBid_tokenBid
          }
        }
      }
    }
    ${AccountEntry.fragments.payment}
    ${TransactionsBid.fragments.tokenBid}
  ` as TypedDocumentNode<Auction_authorizationRequest>,
};
